import { ChangeEvent, Fragment, useEffect, useState } from "react";
import { InvoiceReviewDistrict } from "../../api/models/invoiceReviewDistrict";
import { InvoiceStatus, Role } from "../../common/constants";
import { useHistory } from "react-router-dom";
import ApproveRejectModal from '../approveRejectModal';
import { downloadCostPerChild } from '../../api/services/costPerChildService';
import cx from 'classnames';
import InvoiceReviewRequest from '../../api/requests/invoiceReviewRequest';
import moment from "moment";
import InvoiceExportRequest, { InvoiceExportRequestDistrict } from "../../api/requests/invoiceExportRequest";
import { hasUserRole, hasValue } from '../../common/utils';
import { useAsync } from "react-async";
import Modal from "../modal";

type DistrictsProps = {
    districts: InvoiceReviewDistrict[],
    year: number,
    month: number,
    onReviewCreated: Function;
    onInvoiceExport: Function;
    isDirector: boolean;
}

/**
 * Returns the districts that are marked and ready for export
 * @param districts InvoiceReviewDistrict[] | null
 * @returns InvoiceReveiwDistrict[]
 */
const getDistrictInvoicesToExport = (districts: InvoiceReviewDistrict[] | null) => {
    return districts
        ?.filter(x =>
            x.isMarkedForExport &&
            !x.isExportedAndLocked &&
            ((!x.reviewers || x.reviewers.length === 0) || //Check for reviewers
                (x.reviewers && x.reviewers?.length > 0 &&
                    x.reviewers
                        .filter(x => hasUserRole(x.user.userRoles, Role.Director)) //Filter the director reviewers
                        .filter(x => //If any directors exist, verify they have approved the invoice
                            x.reviews.length > 0 &&
                            x.reviews.filter(y => y.invoiceStatusId !== InvoiceStatus.Approved).length === 0)
                ))) ?? [];
}

const InvoiceDistrictEntries = ({ districts, year, month, onReviewCreated, onInvoiceExport, isDirector }: DistrictsProps) => {
    const history = useHistory();

    const fillExpandedDistricts = () => {
        let tempArray: Number[] = [];
        let index = 0;
        districts.forEach(() => {
            tempArray.push(index++);
        })

        return tempArray;
    }

    const [expandedDistricts, setExpandedDistricts] = useState<Number[]>(fillExpandedDistricts());
    const [selectedDistrict, setSelectedDistrict] = useState<InvoiceReviewDistrict | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<InvoiceStatus | null>(null);
    const [selectedProgramId, setSelectedProgramId] = useState<number>();
    const [exportEnabled, setExportEnabled] = useState<boolean>(getDistrictInvoicesToExport(districts).length > 0);
    const [isCPCOpen, setIsCPCOpen] = useState<boolean>(false);

    const getIsExpandable = (index: number): boolean => {
        return expandedDistricts.some(x => x === index);
    }

    const expandAllFunc = () => {
        if (isAllExpanded()) {
            setExpandedDistricts([]);
        } else {
            setExpandedDistricts(fillExpandedDistricts())
        }
    }

    const isAllExpanded = (): boolean => {
        return expandedDistricts.length === districts.length;
    }

    /**
     * Expand/Collapse the entry for a district
     * @param index 
     */
    const toggleDistrict = (index: number) => {
        let tempArray = [...expandedDistricts];
        let isExpanded = tempArray.some(x => x === index)
        if (isExpanded) {
            setExpandedDistricts(tempArray.filter(x => x !== index));
        } else {
            tempArray.push(index);
            setExpandedDistricts(tempArray);
        }
    }

    const handleReviewCreated = (
        invoiceStatusId: InvoiceStatus,
        district: InvoiceReviewDistrict,
        comment?: string
    ) => {
        const request: InvoiceReviewRequest = {
            year: year,
            month: month,
            invoiceStatusId,
            divisionId: district.divisionId,
            districtId: district.districtId,
            comment,
            programId: district.programId === 0 ? null : district.programId
        };
        onReviewCreated(request);
        setSelectedDistrict(null);
    }

    const handleMarkForExport = (e: ChangeEvent<HTMLInputElement>, district: InvoiceReviewDistrict) => {
        district.isMarkedForExport = e?.target.checked;
        const isExportEnabled = getDistrictInvoicesToExport(districts).length > 0;
        setExportEnabled(isExportEnabled);
    }

    const navigateToDetailReport = (districtId: number, programId: number | undefined = undefined) => {
        history.push(`/accounting/invoice/detail?districtId=${districtId}&year=${year}&month=${month}${programId ? `&programId=${programId}` : ''}`);
    }

    const navigateToSummaryReport = (districtId: number, programId: number | undefined = undefined) => {
        history.push(`/accounting/invoice/summary?districtId=${districtId}&year=${year}&month=${month}${programId ? `&programId=${programId}` : ''}`);
    }

    const navigateToInvoiceDraft = (districtId: number, programId: number | undefined = undefined) => {
        history.push(`/accounting/invoice/draft?districtId=${districtId}&year=${year}&month=${month}${programId ? `&programId=${programId}` : ''}`);
    }

    /*const processInvoiceExports = () => {
        const invoiceDistrictIdsToExport = districts
            ?.filter(d => d.isMarkedForExport)
            .map((d, i) => d.districtId) ?? [];

        const invoiceExportRequest: InvoiceExportRequest = {
            year: year,
            month: month,
            districtIds: invoiceDistrictIdsToExport,
            undoExport: false,
        };
        onInvoiceExport(invoiceExportRequest);
    }*/

    useEffect(() => {
        const isExportEnabled = getDistrictInvoicesToExport(districts).length > 0;
        setExportEnabled(isExportEnabled);
    }, [districts]);

    const downloadCPCReportDefer = async (args: any[]) => {
        return downloadCostPerChild(args[0], month, year, args[1]);
    }

    const downloadCPCReportRequest = useAsync({
        deferFn: downloadCPCReportDefer,
        onResolve: () => {
            setIsCPCOpen(false)
        }
    })

    const handleQuickBooksDateSelected = (
        transactionDate?: Date
    ) => {
        const invoiceDistrictIdsToExport = districts
            ?.filter(d => d.isMarkedForExport)
            .map((d, i) => d.districtId) ?? [];

        const invoiceDistrictsToExport: InvoiceExportRequestDistrict[] = [];

        districts?.filter(d => d.isMarkedForExport).map((d, i) => {
            invoiceDistrictsToExport.push({
                districtId: d.districtId,
                programId: d.programId,
            });
        })

        const invoiceExportRequest: InvoiceExportRequest = {
            year: year,
            month: month,
            districtIds: invoiceDistrictIdsToExport,
            districts: invoiceDistrictsToExport,
            undoExport: false,
            transactionDate: moment(transactionDate?.toDateString()).toDate()
        };

        onInvoiceExport(invoiceExportRequest);
    }

    return (
        <div>
            {/* Invoice proofing review modal */}
            <ApproveRejectModal
                open={selectedDistrict !== null}
                editedEntityName={selectedDistrict?.districtName}
                onCancel={() => {
                    setSelectedDistrict(null)
                    setSelectedStatus(null)
                }}
                invoiceStatus={selectedStatus}
                onApprove={(comment?: string) => handleReviewCreated(
                    InvoiceStatus.Approved, selectedDistrict!, comment)}
                onReject={(comment?: string) => handleReviewCreated(
                    InvoiceStatus.Rejected, selectedDistrict!, comment)}
                onComment={(comment?: string) => handleReviewCreated(
                    InvoiceStatus.Pending, selectedDistrict!, comment)}
            />

            <Modal
                saveText='Ok'
                open={isCPCOpen}
                onCancel={() => { setIsCPCOpen(false) }}
                onSave={() => { setIsCPCOpen(false) }}
            >
                <Fragment>
                    <div className='grid grid-cols-1'>
                        <p>CPC report is being created</p>
                    </div>
                </Fragment>
            </Modal>

            {/* If no districts */}
            {districts?.length <= 0 &&
                <div className='text-center text-gray-500'>
                    No districts in the selected range
                </div>
            }

            {/* Expand all link */}
            {districts?.length > 0 && (
                <div className='invoice-districts-summary'>
                    <div className='flex items-center justify-between'>
                        {!isDirector &&
                            <button className='bg-ptsgreen-normal hover:bg-ptsgreen-dark'
                                onClick={() => handleQuickBooksDateSelected(new Date())}
                                disabled={!exportEnabled}
                            >
                                Export Invoice Data
                            </button>
                        }
                        <span
                            className='cursor-pointer text-ptsblue-normal hover:underline'
                            onClick={() => { expandAllFunc() }}>
                            {isAllExpanded() ? "Collapse All" : "Expand All"}
                        </span>
                    </div>
                    <div className='summary-header text-sm font-bold p-0.5 px-1.5'>
                        <div className="flex items-end">
                            {!isDirector &&
                                <div className='mr-3' title='Mark for export to QuickBooks'>QB</div>
                            }
                            <div className='w-1/4' title='District Name'>
                                <span>District</span>
                            </div>
                            <div className='flex items-end justify-between w-3/4 text-center'>
                                <span className='mx-2 w-14' title='Division ID'>
                                    Division
                                </span>
                                <span className='w-20 mx-2' title='Number of approved timesheets / total number of timesheets'>
                                    Timesheets<br />Approved
                                </span>
                                <span className='mx-2 w-14' title='Number of issue flags on timesheets'>
                                    Issues
                                </span>
                                <span className='mx-2 w-14' title='Ready when all timesheets have been approved and issues have been resolved'>
                                    Ready<br />For<br />Review
                                </span>
                                <span className='w-20 mx-2' title='Current status of invoice'>
                                    Approval<br />Status
                                </span>
                                <span className='w-20 mx-2' title='Links to draft invoice documents'>
                                    Review<br />Documents
                                </span>
                                {!isDirector &&
                                    <span className='w-20 mx-2' title='Status of export to QuickBooks'>
                                        Exported &amp;<br />Locked
                                    </span>
                                }
                                <span className='w-16 mx-2' title='Invoice number generated by QuickBooks'>
                                    Invoice #
                                </span>
                                <span className='w-20 mx-2' title='Links to final invoice documents'>
                                    Invoice<br />Documents
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {districts?.map((district, index) => (
                <div
                    key={`districtWithErrorCount_${district.districtId}_${district.programId}`}
                    id={`districtWithErrorCount_${district.districtId}_${district.programId}`}
                    className='expandable-panel-header bg-gray-100 rounded-lg p-0.5 px-1.5 mb-3'
                >
                    <div onClick={() => toggleDistrict(index)}
                        className='flex items-center cursor-pointer'
                    >
                        {!isDirector &&
                            <input
                                id={`mark-for-export_${index}`}
                                name={`mark-for-export_${index}`}
                                key={`mark-for-export_${district.districtId}_${district.programId}`}
                                title='Mark for export to QuickBooks'
                                type='checkbox'
                                className='mr-4 primary-checkbox disabled:opacity-50 disabled:cursor-not-allowed'
                                disabled={false}
                                checked={district.isMarkedForExport}
                                onChange={(e) => handleMarkForExport(e, district)}
                            />
                        }
                        {/* Group Labels */}
                        <div className='w-1/4 h-full'>
                            <div className={
                                cx({
                                    'text-xl font-bold': true,
                                    'text-red-500': district.issueCount > 0
                                })
                            }>
                                <span className="districtName">{hasValue(district.programName) ? district.programName : district.districtName}</span>
                                {hasValue(district.programName) ? <span className="block text-sm text-gray-500 underline districtProgramName">{district.districtName}</span> : <></>}
                            </div>
                        </div>
                        {/* Totals */}
                        <div className='flex items-center justify-between w-3/4 h-full my-1 text-center'>
                            <div className='mx-2 w-14'>
                                <p>{district.divisionId}</p>
                            </div>
                            <div className='w-20 mx-2'>
                                <p className={
                                    cx({
                                        'text-red-500 font-bold': (district.approvedTimesheets < district.totalTimesheets)
                                    })
                                }>
                                    {district.approvedTimesheets} / {district.totalTimesheets}
                                </p>
                            </div>
                            <div className='mx-2 w-14'>
                                <p className={
                                    cx({
                                        'text-red-500 font-bold': (district.issueCount > 0)
                                    })
                                }>
                                    {district.issueCount}
                                </p>
                            </div>
                            <div className='mx-2 w-14'>
                                <p>
                                    {district.isReadyForReview ? 'Yes' : 'No'}
                                </p>
                            </div>
                            <div className='w-20 mx-2'>
                                <p className={
                                    cx({
                                        'font-bold': true,
                                        'text-yellow-500': (district.invoiceStatusId === InvoiceStatus.Pending),
                                        'text-green-500': (district.invoiceStatusId === InvoiceStatus.Approved),
                                        'text-red-500': (district.invoiceStatusId === InvoiceStatus.Rejected),
                                    })
                                }>
                                    {InvoiceStatus[district.invoiceStatusId]}
                                </p>
                            </div>
                            <div className='w-20 mx-2'>
                                <div className='text-sm'>
                                    <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                        onClick={(e) => navigateToInvoiceDraft(district.districtId, district.programId)}>Invoice Preview</p>
                                    <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.nativeEvent.stopImmediatePropagation();
                                            setIsCPCOpen(true);
                                            downloadCPCReportRequest.run(district.districtId, district.programId)
                                        }}>Draft CPC</p>
                                </div>
                            </div>
                            {!isDirector &&
                                <div className='w-20 mx-2'>
                                    <p>
                                        {district.isExportedAndLocked ? 'Yes, ready for final printouts' : 'No'}
                                    </p>
                                </div>
                            }
                            <div className='w-16 mx-2 break-words'>
                                <p>
                                    {district.invoiceNumber}
                                </p>
                            </div>
                            <div className='w-20 mx-2'>
                                {/* This condition needs to be reinstated after testing! */}
                                {/* {district.isExportedAndLocked && */}
                                <div className='text-sm'>
                                    <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                        onClick={(e) => navigateToSummaryReport(district.districtId, district.programId)}>Print Summary</p>
                                    <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                        onClick={(e) => navigateToDetailReport(district.districtId, district.programId)}>Print Detail</p>
                                    <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.nativeEvent.stopImmediatePropagation();
                                            setIsCPCOpen(true);
                                            downloadCPCReportRequest.run(district.districtId, district.programId)
                                        }}>Final CPC</p>
                                </div>
                                {/* } */}
                            </div>
                        </div>
                    </div>
                    {/* Expandable approvals table */}
                    {getIsExpandable(index) &&
                        <div className='expandable-panel-wrapper'>
                            <button className='mx-2 mb-3 bg-ptsblue-normal hover:bg-ptsblue-dark' style={{ marginLeft: '1.5rem' }}
                                onClick={(e) => {
                                    setSelectedDistrict(district)
                                    setSelectedStatus(InvoiceStatus.Approved)
                                }}>
                                Approve
                            </button>
                            <button className='mx-2 mb-3 bg-red-500 hover:bg-red-700'
                                onClick={(e) => {
                                    setSelectedDistrict(district)
                                    setSelectedStatus(InvoiceStatus.Rejected)
                                }}>
                                Reject
                            </button>
                            <button className='mx-2 mb-3 bg-ptsblue-normal hover:bg-ptsblue-dark'
                                onClick={(e) => {
                                    setSelectedDistrict(district)
                                    setSelectedStatus(InvoiceStatus.Pending)
                                }}>
                                Comment Only
                            </button>
                            <div className='mx-6 my-2 bg-white border border-gray-300 rounded-lg expandable-panel'>
                                <table className='w-11/12 mx-auto my-3 text-sm text-left' cellPadding='4em'>
                                    <thead className='font-bold'>
                                        <tr>
                                            <th className='mx-2 reviewer-role-header'>Role</th>
                                            <th className='mx-2 reviewer-name-header'>Name</th>
                                            <th className='mx-2 reviewer-status-header'>Status</th>
                                            <th className='mx-2 reviewer-comments-header'>Comments</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {district.reviewers?.map((reviewer, index) => {
                                            //If this is a director, verify we have director reviewers.
                                            //If we don't have director reviewers, we don't want to show the directors row
                                            const directors = district.reviewers?.filter(x => x.user.userRoles.some(y => y.role.name === Role.Director));
                                            if (hasUserRole(reviewer.user?.userRoles, Role.Director) && (directors && directors?.length > 0 ? directors[0].user.firstName === '' : true)) return null;

                                            return <tr key={`reviewer_${index}`} style={{ borderTop: '1px solid gray' }}>
                                                <td className='w-32 mx-2 reviewer-role'>{reviewer.user?.userRoles && reviewer.user?.userRoles.length > 0 ? reviewer.user?.userRoles[0].role?.name : 'N/A'}</td>
                                                <td className='mx-2 w-36 reviewer-name'>
                                                    {reviewer.user && (`${reviewer.user.firstName} ${reviewer.user.lastName}`)}
                                                </td>
                                                <td className={
                                                    cx({
                                                        'font-bold mx-2 w-24 reviewer-status': true,
                                                        'text-yellow-500': (reviewer.invoiceStatusId === InvoiceStatus.Pending || (reviewer.invoiceStatusId as number) === 0),
                                                        'text-green-500': (reviewer.invoiceStatusId === InvoiceStatus.Approved),
                                                        'text-red-500': (reviewer.invoiceStatusId === InvoiceStatus.Rejected),
                                                    })
                                                }>
                                                    {InvoiceStatus[reviewer.invoiceStatusId] ?? InvoiceStatus[InvoiceStatus.Pending]}
                                                </td>
                                                <td className='mx-2 reviewer-comments'>
                                                    <ul>
                                                        {reviewer.reviews?.map((review, index) =>
                                                            <li key={`reviewerComment_${index}`} className='list-disc list-inside'>
                                                                <span className={
                                                                    cx({
                                                                        'font-bold': true,
                                                                        'text-yellow-500': (review.invoiceStatusId === InvoiceStatus.Pending),
                                                                        'text-green-500': (review.invoiceStatusId === InvoiceStatus.Approved),
                                                                        'text-red-500': (review.invoiceStatusId === InvoiceStatus.Rejected),
                                                                    })
                                                                }>
                                                                    {InvoiceStatus[review.invoiceStatusId]}
                                                                </span>
                                                                <span>
                                                                    &nbsp;- {moment(review.createdOn).format('M/D/YYYY')}
                                                                </span>
                                                                <span className='break-word'>
                                                                    {(review.comment && `- ${review.comment}`)}
                                                                </span>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </td>
                                            </tr>
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    }
                </div>
            ))}
        </div>
    )
}

export default InvoiceDistrictEntries;